import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppShell } from "@/components/layout/AppShell";
import { ExamSessionsAPI, EXAM_STATUSES } from "@/lib/exam-sessions-api";
import { CampusesAPI } from "@/lib/sprint2-api";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ScrollText, Plus, Calendar, Users, DoorOpen, ArrowRight, Loader2,
  Download, Grid3x3, ShieldCheck, Sparkles, MapPin, Clock, ArrowLeft,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const TONE = {
  slate: "bg-slate-100 text-slate-600",
  sky: "bg-sky-100 text-sky-700",
  amber: "bg-amber-100 text-amber-700",
  emerald: "bg-emerald-100 text-emerald-700",
  rose: "bg-rose-100 text-rose-700",
};

export default function ExamSessions() {
  const [openId, setOpenId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const campusesQ = useQuery({ queryKey: ["campuses"], queryFn: () => CampusesAPI.list() });
  const activeCampus = (campusesQ.data || [])[0];

  const listQ = useQuery({
    queryKey: ["exam-sessions"],
    queryFn: () => ExamSessionsAPI.list(),
  });

  if (openId) {
    return (
      <AppShell>
        <ExamDetail id={openId} onBack={() => setOpenId(null)} />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="exam-sessions-page">
        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="h-8 w-8 rounded-xl bg-violet-600/10 flex items-center justify-center">
                <ScrollText className="h-4 w-4 text-violet-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900">Exam Sessions</h1>
            </div>
            <p className="text-sm text-slate-500 max-w-2xl">
              Schedule exams, register cohorts, auto-allocate exam-ready rooms and generate seat maps.
              Pulls rooms + seat layouts directly from Campus Infrastructure.
            </p>
          </div>
          <Button onClick={() => setShowNew(true)} data-testid="new-exam-btn">
            <Plus className="h-4 w-4 mr-1" /> New Exam
          </Button>
        </header>

        {listQ.isLoading ? (
          <div className="py-12 flex items-center justify-center text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (listQ.data || []).length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 py-16 flex flex-col items-center text-center">
            <div className="h-14 w-14 rounded-xl bg-violet-50 flex items-center justify-center mb-3">
              <ScrollText className="h-6 w-6 text-violet-600" />
            </div>
            <div className="text-base font-semibold text-slate-900">No exams yet</div>
            <p className="text-sm text-slate-500 mt-1 max-w-md">
              Create your first exam and let the system auto-allocate rooms + assign seats in seconds.
            </p>
            <Button className="mt-4" onClick={() => setShowNew(true)}>
              <Plus className="h-4 w-4 mr-1" /> New Exam
            </Button>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="exam-list">
            {(listQ.data || []).map((e) => {
              const st = EXAM_STATUSES.find((s) => s.key === e.status) || EXAM_STATUSES[0];
              return (
                <li
                  key={e.id}
                  onClick={() => setOpenId(e.id)}
                  className="cursor-pointer rounded-xl bg-white border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all p-4 flex items-center gap-4"
                  data-testid={`exam-row-${e.id}`}
                >
                  <div className="h-11 w-11 rounded-xl bg-violet-50 text-violet-600 flex items-center justify-center shrink-0">
                    <ScrollText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-base font-semibold text-slate-900 truncate">{e.name}</div>
                    <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                      {e.exam_date && (<span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {e.exam_date}</span>)}
                      {e.duration_min && (<span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {e.duration_min}m</span>)}
                      {e.board && <span>{e.board}</span>}
                      {e.grades?.length ? <span>Grades: {e.grades.join(", ")}</span> : null}
                    </div>
                  </div>
                  <span className={cn("text-[10px] font-semibold px-2 py-1 rounded-full uppercase tracking-wide", TONE[st.tone])}>
                    {st.label}
                  </span>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {showNew && (
        <NewExamModal
          campusId={activeCampus?.id}
          onClose={() => setShowNew(false)}
          onCreated={(exam) => {
            setShowNew(false);
            setOpenId(exam.id);
          }}
        />
      )}
    </AppShell>
  );
}

function NewExamModal({ campusId, onClose, onCreated }) {
  const [form, setForm] = useState({ name: "", exam_date: "", start_time: "09:00", duration_min: 180, board: "CBSE" });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    if (!campusId) return toast.error("Campus not loaded");
    try {
      const exam = await ExamSessionsAPI.create({ campus_id: campusId, ...form });
      toast.success("Exam created");
      onCreated(exam);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="new-exam-modal">
      <div className="absolute inset-0 bg-slate-900/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div>
          <h3 className="text-lg font-semibold">New Exam Session</h3>
          <p className="text-sm text-slate-500">You&apos;ll register students and allocate rooms in the next screen.</p>
        </div>
        <div>
          <Label className="text-xs mb-1 block">Name *</Label>
          <Input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Term I — Physics"
            data-testid="new-exam-name"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block">Date</Label>
            <Input type="date" value={form.exam_date} onChange={(e) => setForm((f) => ({ ...f, exam_date: e.target.value }))} data-testid="new-exam-date" />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Start Time</Label>
            <Input type="time" value={form.start_time} onChange={(e) => setForm((f) => ({ ...f, start_time: e.target.value }))} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs mb-1 block">Duration (min)</Label>
            <Input type="number" value={form.duration_min} onChange={(e) => setForm((f) => ({ ...f, duration_min: Number(e.target.value) || 0 }))} />
          </div>
          <div>
            <Label className="text-xs mb-1 block">Board</Label>
            <Input value={form.board} onChange={(e) => setForm((f) => ({ ...f, board: e.target.value }))} />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} data-testid="new-exam-save">Create</Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail view — the workflow: Register → Allocate → Assign → Chart
// ---------------------------------------------------------------------------
function ExamDetail({ id, onBack }) {
  const qc = useQueryClient();
  const [step, setStep] = useState("workflow"); // workflow | chart

  const examQ = useQuery({ queryKey: ["exam-session", id], queryFn: () => ExamSessionsAPI.get(id) });
  const classesQ = useQuery({
    queryKey: ["classes"],
    queryFn: () => api.get("/classes").then((r) => r.data),
  });
  const chartQ = useQuery({
    queryKey: ["exam-chart", id],
    queryFn: () => ExamSessionsAPI.seatingChart(id),
    enabled: step === "chart",
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["exam-session", id] });
    qc.invalidateQueries({ queryKey: ["exam-sessions"] });
    qc.invalidateQueries({ queryKey: ["exam-chart", id] });
  };

  const register = useMutation({
    mutationFn: (classIds) => ExamSessionsAPI.registerStudents(id, { class_ids: classIds }),
    onSuccess: (data) => { toast.success(`Registered ${data.registered} students`); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const allocate = useMutation({
    mutationFn: () => ExamSessionsAPI.allocateRooms(id, {}),
    onSuccess: (data) => { toast.success(`Allocated ${data.allocated_rooms} rooms · ${data.total_seats} seats`); invalidate(); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const assign = useMutation({
    mutationFn: () => ExamSessionsAPI.assignSeats(id, { strategy: "random" }),
    onSuccess: (data) => { toast.success(`Assigned ${data.assigned} · ${data.unassigned} unassigned`); invalidate(); setStep("chart"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  if (examQ.isLoading || !examQ.data) {
    return (
      <div className="p-6 flex items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  const exam = examQ.data;
  const st = EXAM_STATUSES.find((s) => s.key === exam.status) || EXAM_STATUSES[0];
  const nStudents = exam.students?.length || 0;
  const nRooms = exam.rooms?.length || 0;
  const seatCount = Object.values(exam.seat_map || {}).reduce((n, arr) => n + (arr?.length || 0), 0);
  const totalCap = (exam.rooms || []).reduce((n, r) => n + (r.exam_capacity || 0), 0);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-6" data-testid="exam-detail">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="h-9 w-9 rounded-lg hover:bg-slate-100 flex items-center justify-center" data-testid="exam-back">
          <ArrowLeft className="h-4 w-4 text-slate-500" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-slate-900">{exam.name}</h1>
            <span className={cn("text-[10px] font-semibold px-2 py-1 rounded-full uppercase", TONE[st.tone])}>{st.label}</span>
          </div>
          <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
            {exam.exam_date && (<span className="inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> {exam.exam_date}</span>)}
            {exam.start_time && (<span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {exam.start_time}</span>)}
            {exam.duration_min && <span>{exam.duration_min} min</span>}
            {exam.board && <span>{exam.board}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={ExamSessionsAPI.hallTicketsUrl(id)}
            target="_blank"
            rel="noreferrer"
            className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 hover:bg-slate-50 inline-flex items-center gap-1.5"
            data-testid="hall-tickets-download"
          >
            <Download className="h-3.5 w-3.5" /> Hall Tickets CSV
          </a>
          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
            <button
              onClick={() => setStep("workflow")}
              className={cn("px-3 py-1 text-sm rounded-md", step === "workflow" ? "bg-white shadow" : "text-slate-500")}
              data-testid="tab-workflow"
            >
              Workflow
            </button>
            <button
              onClick={() => setStep("chart")}
              className={cn("px-3 py-1 text-sm rounded-md", step === "chart" ? "bg-white shadow" : "text-slate-500")}
              data-testid="tab-chart"
            >
              Seating Chart
            </button>
          </div>
        </div>
      </div>

      {step === "workflow" ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <WorkflowCard
            n={1}
            icon={Users}
            title="Register Students"
            count={nStudents}
            countLabel="registered"
          >
            <ClassPicker
              classes={classesQ.data || []}
              onRegister={(ids) => register.mutate(ids)}
              busy={register.isPending}
            />
          </WorkflowCard>

          <WorkflowCard
            n={2}
            icon={DoorOpen}
            title="Allocate Rooms"
            count={nRooms}
            countLabel={`rooms · ${totalCap} seats`}
          >
            <div className="text-xs text-slate-500 mb-2">
              Auto-picks exam-ready rooms until we can seat every registered student.
            </div>
            <Button
              onClick={() => allocate.mutate()}
              disabled={nStudents === 0 || allocate.isPending}
              size="sm"
              className="w-full"
              data-testid="allocate-btn"
            >
              <Sparkles className="h-3.5 w-3.5 mr-1" />
              {allocate.isPending ? "Allocating…" : nRooms > 0 ? "Re-allocate rooms" : "Auto-allocate rooms"}
            </Button>
            {exam.rooms?.length > 0 && (
              <ul className="mt-3 space-y-1 text-xs">
                {exam.rooms.slice(0, 4).map((r) => (
                  <li key={r.id} className="flex items-center gap-1.5 text-slate-600">
                    <MapPin className="h-3 w-3 text-slate-400" />
                    {r.name} · {r.exam_capacity} seats {r.layout?.grid ? <Grid3x3 className="h-3 w-3 text-violet-500" title="Has layout" /> : null}
                  </li>
                ))}
              </ul>
            )}
          </WorkflowCard>

          <WorkflowCard
            n={3}
            icon={Grid3x3}
            title="Assign Seats"
            count={seatCount}
            countLabel="seated"
          >
            <div className="text-xs text-slate-500 mb-2">
              Places students into room seat cells (uses saved room layouts when available).
            </div>
            <Button
              onClick={() => assign.mutate()}
              disabled={nRooms === 0 || nStudents === 0 || assign.isPending}
              size="sm"
              className="w-full"
              data-testid="assign-btn"
            >
              <ShieldCheck className="h-3.5 w-3.5 mr-1" />
              {assign.isPending ? "Assigning…" : "Auto-assign seats"}
            </Button>
            {exam.unassigned_ids?.length > 0 && (
              <div className="mt-2 text-[11px] text-rose-600">
                ⚠ {exam.unassigned_ids.length} students still unassigned — add more rooms.
              </div>
            )}
          </WorkflowCard>
        </div>
      ) : (
        <SeatingChartView chart={chartQ.data} isLoading={chartQ.isLoading} />
      )}
    </div>
  );
}

function WorkflowCard({ n, icon: Icon, title, count, countLabel, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-8 w-8 rounded-full bg-violet-600 text-white text-xs font-bold flex items-center justify-center">{n}</div>
        <div className="flex-1">
          <div className="text-sm font-semibold text-slate-900">{title}</div>
        </div>
        <Icon className="h-4 w-4 text-slate-400" />
      </div>
      <div className="mb-3 pb-3 border-b border-slate-100">
        <div className="text-3xl font-bold text-slate-900">{count}</div>
        <div className="text-[11px] uppercase tracking-wide text-slate-500">{countLabel}</div>
      </div>
      {children}
    </div>
  );
}

function ClassPicker({ classes, onRegister, busy }) {
  const [selected, setSelected] = useState("");
  return (
    <div className="space-y-2">
      <Select value={selected} onValueChange={setSelected}>
        <SelectTrigger data-testid="class-select"><SelectValue placeholder="Choose class…" /></SelectTrigger>
        <SelectContent>
          {classes.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name || c.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        onClick={() => selected && onRegister([selected])}
        disabled={!selected || busy}
        size="sm"
        className="w-full"
        data-testid="register-btn"
      >
        <Users className="h-3.5 w-3.5 mr-1" />
        {busy ? "Registering…" : "Register cohort"}
      </Button>
    </div>
  );
}

function SeatingChartView({ chart, isLoading }) {
  if (isLoading || !chart) {
    return (
      <div className="py-12 flex items-center justify-center text-slate-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!chart.rooms?.length) {
    return (
      <div className="rounded-xl border-2 border-dashed border-slate-200 py-10 text-center text-sm text-slate-500">
        No rooms assigned yet — go back to the workflow tab.
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {chart.rooms.map((r) => (
        <RoomChart key={r.room_id} room={r} />
      ))}
      {chart.unassigned?.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
          <div className="text-sm font-semibold text-rose-700 mb-1">
            {chart.unassigned.length} student(s) unassigned
          </div>
          <div className="text-xs text-rose-600">
            Allocate additional rooms and re-run seat assignment.
          </div>
        </div>
      )}
    </div>
  );
}

function RoomChart({ room }) {
  const layout = room.layout;
  // Map each seat assignment to (row, col)
  const seatByPos = new Map();
  for (const s of room.seats || []) {
    seatByPos.set(`${s.row}-${s.col}`, s);
  }

  // Choose grid: use the room's layout if it has cells, else a virtual √cap grid.
  let rows = layout?.rows || 0;
  let cols = layout?.cols || 0;
  let grid = layout?.grid;
  if (!grid || !grid.length) {
    // Build a virtual grid from seat assignments (they were placed at synthetic positions)
    let maxR = 0, maxC = 0;
    for (const s of room.seats || []) { if (s.row > maxR) maxR = s.row; if (s.col > maxC) maxC = s.col; }
    rows = maxR + 1;
    cols = maxC + 1;
    grid = Array.from({ length: rows }, () => Array.from({ length: cols }, () => ({ type: "empty" })));
    for (const s of room.seats || []) grid[s.row][s.col] = { type: "seat" };
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-base font-semibold text-slate-900">{room.room_name}</div>
          <div className="text-xs text-slate-500">
            {(room.seats || []).length} of {room.exam_capacity} seats filled
            {layout ? " · using saved room layout" : " · virtual layout (no room diagram saved)"}
          </div>
        </div>
      </div>
      <div className="text-center text-[10px] uppercase tracking-widest font-semibold text-slate-400 mb-2">
        ← Front of the room →
      </div>
      <div
        className="grid gap-1 mx-auto w-fit"
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(2.75rem, 3rem))` }}
      >
        {grid.map((row, r) =>
          row.map((cell, c) => {
            const assigned = seatByPos.get(`${r}-${c}`);
            const type = cell?.type || "empty";
            if (type === "seat") {
              return (
                <div
                  key={`${r}-${c}`}
                  className={cn(
                    "aspect-square rounded-md border text-[9px] font-semibold flex flex-col items-center justify-center p-1",
                    assigned
                      ? "bg-emerald-50 border-emerald-300 text-emerald-900"
                      : "bg-white border-slate-200 text-slate-400"
                  )}
                  title={assigned?.student ? `${assigned.student.name} · ${assigned.student.roll_no || ""}` : "Empty seat"}
                >
                  {assigned ? (
                    <>
                      <div className="text-[9px] leading-tight truncate w-full text-center">{assigned.student?.roll_no || "—"}</div>
                      <div className="text-[7px] text-emerald-700 truncate w-full text-center">Seat {assigned.seat_no}</div>
                    </>
                  ) : (
                    <span className="text-[10px]">—</span>
                  )}
                </div>
              );
            }
            // Non-seat cells rendered subtly
            const tone =
              type === "teacher" ? "bg-amber-50 border-amber-200 text-amber-700" :
              type === "blocked" ? "bg-rose-50 border-rose-200 text-rose-700" :
              type === "door" || type === "exit" ? "bg-emerald-50 border-emerald-200 text-emerald-700" :
              type === "projector" ? "bg-violet-50 border-violet-200 text-violet-700" :
              type === "window" ? "bg-cyan-50 border-cyan-200 text-cyan-700" :
              type === "wheelchair" ? "bg-teal-50 border-teal-200 text-teal-700" :
              "bg-slate-50 border-slate-200 text-slate-400";
            return (
              <div
                key={`${r}-${c}`}
                className={cn("aspect-square rounded-md border text-[9px] flex items-center justify-center capitalize", tone)}
                title={type}
              >
                {type !== "empty" ? type.slice(0, 4) : ""}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
